/**
 * SONE editor — presentation commands.
 *
 * How a block looks, as opposed to what it is. The three attributes are shared
 * by every block type (BLOCK_ATTRS in @sone/core), so this file has no per-type
 * cases and gains none when a new block is added.
 */
import { BLOCK_ATTRS, } from '@sone/core';
/**
 * Set how the blocks in the selection are presented.
 *
 * `null` clears a setting, which is not the same as choosing a default value:
 * cleared means "as the design decides", so a later change to the design
 * reaches the block. A block that had `align: 'start'` written into it would
 * keep pointing left for ever, even if the design moved on.
 *
 * Applies to every block the selection touches, so setting a width on three
 * selected paragraphs does not silently do one.
 *
 * `tone` and `source` are the two that are not shared by every type (ADR-0188):
 * a tone belongs to a callout and a source to a quote. They are written only
 * to blocks whose type declares the attribute, so setting a tone across a
 * mixed selection tones the callouts in it and leaves the paragraphs alone —
 * ProseMirror would drop the unknown attribute anyway, but silently, and a
 * command should know what it is doing.
 */
export function setBlockStyle(changes) {
    return (state, dispatch) => {
        const { from, to } = state.selection;
        const targets = [];
        state.doc.nodesBetween(from, to, (node, pos) => {
            // Only top-level blocks: a table cell's paragraph is inside the table,
            // and aligning it independently would fight the table's own layout.
            if (node.isBlock && node.type.spec.group?.includes('block')) {
                targets.push({ pos, node });
            }
            return true;
        });
        if (targets.length === 0)
            return false;
        if (!dispatch)
            return true;
        const tr = state.tr;
        for (const target of targets) {
            const attrs = { ...target.node.attrs };
            if ('align' in changes)
                attrs[BLOCK_ATTRS.align] = changes.align ?? null;
            if ('width' in changes)
                attrs[BLOCK_ATTRS.width] = changes.width ?? null;
            if ('color' in changes)
                attrs[BLOCK_ATTRS.color] = changes.color ?? null;
            const declares = (name) => target.node.type.spec.attrs?.[name] !== undefined;
            if ('tone' in changes && declares(BLOCK_ATTRS.tone)) {
                // `note` is the default and is stored as its absence, like a cleared
                // colour: a callout that says "note" out loud cannot follow a design
                // that later decides what a plain callout looks like.
                attrs[BLOCK_ATTRS.tone] = changes.tone && changes.tone !== 'note' ? changes.tone : null;
            }
            if ('source' in changes && declares(BLOCK_ATTRS.source)) {
                const trimmed = changes.source?.trim() ?? '';
                attrs[BLOCK_ATTRS.source] = trimmed === '' ? null : trimmed;
            }
            // The divider's three (ADR-0189), each with its default stored as absence.
            if ('rule' in changes && declares(BLOCK_ATTRS.rule)) {
                attrs[BLOCK_ATTRS.rule] = changes.rule && changes.rule !== 'solid' ? changes.rule : null;
            }
            if ('ornament' in changes && declares(BLOCK_ATTRS.ornament)) {
                attrs[BLOCK_ATTRS.ornament] = changes.ornament ?? null;
                // No symbol, no place for it.
                if (!changes.ornament)
                    attrs[BLOCK_ATTRS.ornamentAt] = null;
            }
            if ('ornamentAt' in changes && declares(BLOCK_ATTRS.ornamentAt)) {
                attrs[BLOCK_ATTRS.ornamentAt] =
                    changes.ornamentAt && changes.ornamentAt !== 'center' ? changes.ornamentAt : null;
            }
            tr.setNodeMarkup(target.pos, undefined, attrs);
        }
        dispatch(tr);
        return true;
    };
}
/** What the blocks in the selection currently carry, when they agree. */
export function currentBlockStyle(state) {
    const { from, to } = state.selection;
    let align = null;
    let width = null;
    let color = null;
    let tone = null;
    let source = null;
    let rule = null;
    let ornament = null;
    let ornamentAt = null;
    let seen = false;
    state.doc.nodesBetween(from, to, (node) => {
        if (!node.isBlock || !node.type.spec.group?.includes('block'))
            return true;
        const nodeAlign = node.attrs[BLOCK_ATTRS.align] ?? null;
        const nodeWidth = node.attrs[BLOCK_ATTRS.width] ?? null;
        const nodeColor = node.attrs[BLOCK_ATTRS.color] ?? null;
        const nodeTone = node.attrs[BLOCK_ATTRS.tone] ?? null;
        const nodeSource = node.attrs[BLOCK_ATTRS.source] ?? null;
        const nodeRule = node.attrs[BLOCK_ATTRS.rule] ?? null;
        const nodeOrnament = node.attrs[BLOCK_ATTRS.ornament] ?? null;
        const nodeOrnamentAt = node.attrs[BLOCK_ATTRS.ornamentAt] ?? null;
        if (!seen) {
            align = nodeAlign;
            width = nodeWidth;
            color = nodeColor;
            tone = nodeTone;
            source = nodeSource;
            rule = nodeRule;
            ornament = nodeOrnament;
            ornamentAt = nodeOrnamentAt;
            seen = true;
            return true;
        }
        // Blocks that disagree report nothing rather than the first one's answer,
        // which would show a setting the other blocks do not have.
        if (nodeAlign !== align)
            align = null;
        if (nodeWidth !== width)
            width = null;
        if (nodeColor !== color)
            color = null;
        if (nodeTone !== tone)
            tone = null;
        if (nodeSource !== source)
            source = null;
        if (nodeRule !== rule)
            rule = null;
        if (nodeOrnament !== ornament)
            ornament = null;
        if (nodeOrnamentAt !== ornamentAt)
            ornamentAt = null;
        return true;
    });
    return { align, width, color, tone, source, rule, ornament, ornamentAt };
}
/**
 * Insert a file block.
 *
 * The default display depends on what the file is, because the useful default
 * differs: a PDF is usually attached to be read, so it opens as a viewer, while
 * a spreadsheet nothing here can render is a card with its name on it. Somebody
 * can change it either way — this only decides which is right more often.
 */
export function insertFileBlock(attrs) {
    return (state, dispatch) => {
        const type = state.schema.nodes['file'];
        if (!type)
            return false;
        if (!dispatch)
            return true;
        const display = attrs.display ?? (attrs.category === 'pdf' || attrs.category === 'text' ? 'full' : 'card');
        dispatch(state.tr.replaceSelectionWith(type.create({
            fileId: attrs.fileId,
            filename: attrs.filename,
            mimeType: attrs.mimeType,
            category: attrs.category,
            sizeBytes: attrs.sizeBytes ?? null,
            display,
        })));
        return true;
    };
}
/**
 * Insert a video block.
 *
 * One command for all three sources, as there is one node: what differs between
 * an upload, an embed and a stream is which attribute carries the address, not
 * what kind of thing is being put in the page.
 *
 * The player is the default in every case, which is what was asked for and is
 * also the only honest default — somebody inserting a video means to show a
 * video, and a card is what they choose afterwards if the page is a list of
 * links.
 */
export function insertVideoBlock(attrs) {
    return (state, dispatch) => {
        const type = state.schema.nodes['video'];
        if (!type)
            return false;
        if (!dispatch)
            return true;
        dispatch(state.tr.replaceSelectionWith(type.create({
            source: attrs.source,
            fileId: attrs.fileId ?? null,
            url: attrs.url ?? '',
            title: attrs.title ?? '',
            display: attrs.display ?? 'player',
        })));
        return true;
    };
}
/**
 * Change how the video block at `pos` is drawn.
 *
 * A stream refuses anything but the player, here rather than only in the menu:
 * the menu is one caller, and a card for something that is interesting only
 * while it is live is a dead link tomorrow whoever asked for it.
 */
export function setVideoDisplay(pos, display) {
    return (state, dispatch) => {
        const node = state.doc.nodeAt(pos);
        if (!node || node.type.name !== 'video')
            return false;
        if (node.attrs['source'] === 'stream' && display !== 'player')
            return false;
        if (!dispatch)
            return true;
        dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, display }));
        return true;
    };
}
/** Change how the file block at `pos` is drawn. */
export function setFileDisplay(pos, display) {
    return (state, dispatch) => {
        const node = state.doc.nodeAt(pos);
        if (!node || node.type.name !== 'file')
            return false;
        if (!dispatch)
            return true;
        dispatch(state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, display }));
        return true;
    };
}
/**
 * Show an image as a card or a line, and back.
 *
 * An image *is* a file with a special way of being drawn, so the card and the
 * line it can take are the file block's own — reached by making the block a
 * file block rather than by teaching a second component the same three layouts.
 * That duplication is what the file block's `···` menu was removed to undo.
 *
 * The conversion is lossless in the direction that matters: the file id and the
 * name survive both ways, and the block attributes — alignment, width, colour —
 * are carried across, because they are somebody's choices about this block and
 * not about which node type happens to hold it.
 */
export function showImageAs(pos, display) {
    return (state, dispatch) => {
        const node = state.doc.nodeAt(pos);
        if (!node)
            return false;
        const isImage = node.type.name === 'image';
        const isImageFile = node.type.name === 'file' && node.attrs['category'] === 'image';
        if (!isImage && !isImageFile)
            return false;
        const wantsImage = display === 'image';
        if (wantsImage === isImage) {
            // Already the right node type; only the file block's own display needs
            // changing, and an image node has none to change.
            if (isImage)
                return true;
            return setFileDisplay(pos, display === 'card' ? 'card' : 'line')(state, dispatch);
        }
        const target = state.schema.nodes[wantsImage ? 'image' : 'file'];
        if (!target)
            return false;
        // Shared block attributes travel; the rest is rebuilt from what each node
        // type needs.
        const carried = {
            [BLOCK_ATTRS.id]: node.attrs[BLOCK_ATTRS.id],
            [BLOCK_ATTRS.indent]: node.attrs[BLOCK_ATTRS.indent],
            [BLOCK_ATTRS.align]: node.attrs[BLOCK_ATTRS.align],
            [BLOCK_ATTRS.width]: node.attrs[BLOCK_ATTRS.width],
            [BLOCK_ATTRS.color]: node.attrs[BLOCK_ATTRS.color],
        };
        const url = isImage
            ? node.attrs['url']
            : `/api/files/${String(node.attrs['fileId'] ?? '')}`;
        const fileId = isImage ? fileIdFrom(url) : node.attrs['fileId'];
        const name = isImage
            ? node.attrs['alt'] || 'Image'
            : node.attrs['filename'];
        // An image that is not one of ours — pasted from elsewhere, or still
        // uploading — has no file id, and a card for it could not be opened or
        // downloaded. Refused rather than half-built.
        //
        // Checked before the dispatch guard, or the command reports that it can act
        // on something it will refuse: a menu asking "is this available" would show
        // the entry and then do nothing.
        if (!fileId)
            return false;
        if (!dispatch)
            return true;
        const next = wantsImage
            ? target.create({ ...carried, url: `/api/files/${fileId}`, alt: name })
            : target.create({
                ...carried,
                fileId,
                filename: name,
                mimeType: 'image/*',
                category: 'image',
                display,
            });
        dispatch(state.tr.replaceWith(pos, pos + node.nodeSize, next));
        return true;
    };
}
/** The id in `/api/files/<id>`, or null for a URL that is not ours. */
function fileIdFrom(url) {
    const match = /^\/api\/files\/([^/?#]+)/.exec(url ?? '');
    return match?.[1] ?? null;
}
//# sourceMappingURL=commands.js.map